import React, { useEffect, useLayoutEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import bgPattern from "../assets/pattern-square.svg";
import Logo from "../assets/logo.png";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { notifyError, notifySuccess } from "../utils/Toastify";

const SkeletonBox = ({ className }: { className?: string }) => (
  <div className={`bg-gray-700 animate-pulse rounded ${className}`} />
);

const aesSalt = process.env.REACT_APP_AES_SALT as string;
const singleLoginBEUrl = process.env.REACT_APP_SINGLELOGIN_BE_REDIRECT_URL;
const moduleLink = process.env.REACT_APP_FE_MODULE_URL;
const singleLoginRedirectUrl =
  process.env.REACT_APP_SINGLELOGIN_FE_REDIRECT_URL;
const redirectLink = process.env.REACT_APP_REDIRECT_URL;

const encryptPassword = (plaintext: string) => {
  const password = CryptoJS.AES.encrypt(plaintext, aesSalt).toString();
  return password;
};

type Business = {
  id: string;
  name: string;
  email: string;
};

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState<"credentials" | "keys">("keys");
  const [publicKey, setPublicKey] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [showBusinessList, setShowBusinessList] = useState(false);
  const { verifyUserApi, isAuthenticated, autoLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleMessage = async (event: any) => {
      try {
        if (!singleLoginRedirectUrl) {
          return;
        }

        let expectedOrigin;
        try {
          const urlObj = new URL(singleLoginRedirectUrl);
          expectedOrigin = urlObj.origin;
        } catch (error) {
          return;
        }

        if (event.origin !== expectedOrigin) {
          return;
        }

        let data;
        if (typeof event.data === "string") {
          try {
            data = JSON.parse(event.data);
          } catch {
            data = undefined;
          }
        } else if (typeof event.data === "object") {
          data = event.data;
        } else {
          return;
        }

        if (data?.token) {
          localStorage.setItem("token", data.token);
        }

        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response_ = await verifyUserApi(token);
        notifySuccess("You have successfully logged in.");
      } catch (e) {}
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [navigate]);

  if (isAuthenticated) {
    return <Navigate to="/chat" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (activeTab === "credentials") {
        const [idToken, businessesList] = await loginWithEmailPassword(
          username,
          password
        );
        if (idToken) {
          localStorage.setItem("token", idToken);
        }
        setBusinesses(businessesList);
        setShowBusinessList(true);
      } else {
        // keys login flow (minimal implementation)
        if (!publicKey || !privateKey) {
          notifyError("Both public and private keys are required");
          return;
        }

        try {
          const res = await api.post("/auth/login-with-keys", {
            publicKey,
            privateKey,
            moduleUrl: moduleLink,
          });
          const data = res.data;
          const token = data?.access_token || data?.token || data?.idToken;

          if (!token) throw new Error("No token received from server");

          notifySuccess("Authentication Successful");

          autoLogin({ verifiedToken: token, user: data.user });
          navigate("/chat", { replace: true });
        } catch (err: any) {
          notifyError(
            err?.response?.data?.error?.message ||
              err?.message ||
              "Authentication failed. Please try again."
          );
        }
      }
    } catch (error: any) {
      notifyError(
        error.response?.data?.error?.message || "Invalid credentials"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithEmailPassword = async (email: string, password: string) => {
    try {
      // Encrypt the password before sending
      const encryptedPassword = encryptPassword(password);

      const response = await fetch(
        `${singleLoginBEUrl}/users/login-with-email-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            password: encryptedPassword,
            moduleUrl: moduleLink,
          }),
        }
      );

      const data = await response.json();

      if (data.businessesList) {
        return [data.idToken, data.businessesList];
      } else {
        throw new Error("No businesses received from server");
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleBusinessSelect = async (business: Business) => {
    try {
      setIsLoading(true);

      // Get the token from localStorage
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Make the API call to get module authentication token
      const response = await fetch(`${singleLoginBEUrl}/users/module-auth`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          moduleUrl: moduleLink,
          businessId: business.id,
        }),
      });

      const data = await response.json();

      // Get the module auth token from response
      const moduleAuthToken = data.token;
      localStorage.setItem("verify-chatbot-token", moduleAuthToken);

      if (!moduleAuthToken) {
        throw new Error("No token received from module authentication");
      }
      const verifyResponse = await verifyUserApi(data.token);
      localStorage.setItem("token", verifyResponse.access_token);

      notifySuccess("You have successfully logged in.");
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleSingleLoginRedirect = () => {
    window.open(
      `${singleLoginRedirectUrl}/?moduleurl=${moduleLink}&redirecturl=${redirectLink}`,
      "SingleLoginPopup",
      "width=500,height=600,left=0,top=0,resizable,scrollbars"
    );
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#020617] py-12 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundImage: `url(${bgPattern})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
      }}
    >
      <div className="w-full max-w-[500px] lg:px-0 px-3">
        <div className="text-center mb-8">
          <img src={Logo} alt="" className="w-[70px] mx-auto" />
          <h2 className="text-[40px] font-semibold text-white mb-2 mt-8">
            MicroAgent Chatbot
          </h2>
          <p className="text-[#64748b]">
            Sign in to access your AI-powered document assistant
          </p>
        </div>

        <div
          className="rounded-lg p-6 border-[#334155] border mx-auto"
          style={{ backgroundColor: "#1e293b" }}
        >
          {!showBusinessList ? (
            <>
              <form className="space-y-6 " onSubmit={handleSubmit}>
                <div className="flex mb-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab("credentials")}
                    className={`flex-1 py-3 px-4 text-sm font-semibold rounded-l-lg border transition-colors ${
                      activeTab === "credentials"
                        ? "bg-[#8b3dff] text-white border-[#8b3dff]"
                        : "bg-[#334155] text-[#64748b] border-[#334155] hover:bg-[#475569]"
                    }`}
                  >
                    Credentials
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("keys")}
                    className={`flex-1 py-3 px-4 text-sm font-semibold rounded-r-lg border transition-colors ${
                      activeTab === "keys"
                        ? "bg-[#8b3dff] text-white border-[#8b3dff]"
                        : "bg-[#334155] text-[#64748b] border-[#334155] hover:bg-[#475569]"
                    }`}
                  >
                    Keys
                  </button>
                </div>

                {activeTab === "credentials" ? (
                  <>
                    <div>
                      <label
                        htmlFor="username"
                        className="block text-sm font-medium text-[#64748b] mb-2"
                      >
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="username"
                        name="username"
                        type="text"
                        required
                        className="h-[45px] w-full px-4 py-2 text-[13px] rounded-lg text-white placeholder-gray-500 border-[#334155] border focus:outline-[#8b3dff] focus:ring-2 pr-10"
                        style={{ backgroundColor: "#020617" }}
                        placeholder="Enter your email"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-[#64748b] mb-2"
                      >
                        Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative w-full">
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          required
                          className="h-[45px] w-full px-4 py-2 text-[13px] rounded-lg text-white placeholder-gray-500 border-[#334155] border focus:outline-[#8b3dff] focus:ring-2 pr-10"
                          style={{ backgroundColor: "#020617" }}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={isLoading}
                        />

                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label
                        htmlFor="publicKey"
                        className="block text-sm font-medium text-[#64748b] mb-2"
                      >
                        Public Key <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="publicKey"
                        type="text"
                        value={publicKey}
                        onChange={(e) => setPublicKey(e.target.value)}
                        placeholder="Enter Public Key"
                        required
                        className="h-[45px] w-full px-4 py-2 text-[13px] rounded-lg text-white placeholder-gray-500 border-[#334155] border focus:outline-[#8b3dff] focus:ring-2"
                        style={{ backgroundColor: "#020617" }}
                        disabled={isLoading}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="privateKey"
                        className="block text-sm font-medium text-[#64748b] mb-2"
                      >
                        Private Key <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="privateKey"
                        type="text"
                        value={privateKey}
                        onChange={(e) => setPrivateKey(e.target.value)}
                        placeholder="Enter Private Key"
                        required
                        className="h-[45px] w-full px-4 py-2 text-[13px] rounded-lg text-white placeholder-gray-500 border-[#334155] border focus:outline-[#8b3dff] focus:ring-2 pr-10"
                        style={{ backgroundColor: "#020617" }}
                        disabled={isLoading}
                      />
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={
                    isLoading ||
                    (activeTab === "credentials" && (!username || !password)) ||
                    (activeTab === "keys" && (!publicKey || !privateKey))
                  }
                  className="w-full py-2 px-4 rounded-lg text-white font-semibold transition-colors duration-200 disabled:opacity-50 flex items-center justify-center"
                  style={{ backgroundColor: "#8b3dff" }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) =>
                    (e.currentTarget.style.backgroundColor = "#7c3aed")
                  }
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) =>
                    (e.currentTarget.style.backgroundColor = "#8b3dff")
                  }
                >
                  {isLoading ? (
                    <Loader2 className="text-white animate-spin" />
                  ) : (
                    "Login"
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleSingleLoginRedirect}
                  className="w-full py-2 px-4 rounded-lg text-white font-semibold"
                  style={{ backgroundColor: "#64748b" }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) =>
                    (e.currentTarget.style.backgroundColor = "#56606b")
                  }
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) =>
                    (e.currentTarget.style.backgroundColor = "#64748b")
                  }
                >
                  Sign in with SingleLogin.io
                </button>
              </form>
            </>
          ) : (
            <div>
              <h2 className="text-xl font-semibold text-white text-center mb-6">
                Select Your Business
              </h2>

              <div className="space-y-3">
                {isLoading
                  ? // Show skeleton placeholders
                    Array.from({ length: businesses?.length }).map((_, idx) => (
                      <div
                        key={idx}
                        className="border border-[#334155] rounded-lg p-4"
                        style={{ backgroundColor: "#020617" }}
                      >
                        <div className="text-center space-y-2">
                          <SkeletonBox className="h-6 w-32 mx-auto" />
                          <SkeletonBox className="h-4 w-40 mx-auto" />
                        </div>
                      </div>
                    ))
                  : // Show actual business list
                    businesses.map((business) => (
                      <div
                        key={business.id}
                        onClick={() => handleBusinessSelect(business)}
                        className="cursor-pointer border border-[#334155] rounded-lg p-4 hover:border-[#8b3dff] hover:bg-[#334155] transition-all duration-200"
                        style={{ backgroundColor: "#020617" }}
                      >
                        <div className="text-center">
                          <span className="text-white font-semibold text-lg">
                            {business.name}
                          </span>
                          {business.email && (
                            <div className="text-[#64748b] text-sm mt-1">
                              {business.email}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
              </div>

              <button
                onClick={() => {
                  setShowBusinessList(false);
                  setBusinesses([]);
                }}
                className="w-full mt-4 py-2 px-4 rounded-lg text-[#64748b] font-semibold border border-[#334155] hover:bg-[#334155] transition-colors"
              >
                Back to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
